"use client"

import { useFeatureFlag, logFeatureError } from "@/lib/feature-flags"
import { useAuthStore } from "@/lib/auth-store"
import { Component, type ErrorInfo, type ReactNode } from "react"

interface FeatureFlagWrapperProps {
  featureName: string
  children: ReactNode
  fallback?: ReactNode
}

export function FeatureFlagWrapper({ featureName, children, fallback }: FeatureFlagWrapperProps) {
  const { enabled, loading } = useFeatureFlag(featureName)

  if (loading) return null
  if (!enabled) return fallback || null

  return <FeatureErrorBoundary featureName={featureName}>{children}</FeatureErrorBoundary>
}

interface ErrorBoundaryProps {
  featureName: string
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

class FeatureErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { user } = useAuthStore.getState()
    logFeatureError(this.props.featureName, error, user?.id)
    console.error(`[v0] Feature ${this.props.featureName} error:`, error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return null
    }

    return this.props.children
  }
}
